import React, { useState, useMemo, useEffect, useRef } from "react";
import Modal from "../common/Modal";
import { FLAG_CODES, FLAG_LABELS } from "../../utils/flagLabels";

function UserCountryPickerModal({ open, onClose, onSelect, selectedCode }) {
  const [filter, setFilter] = useState("");

  const sortedCodes = useMemo(() => {
    // Always sort the full code list alphabetically by label
    return FLAG_CODES.slice().sort((a, b) => {
      const labelA = (FLAG_LABELS[a] || a).toLowerCase();
      const labelB = (FLAG_LABELS[b] || b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, []);

  const filteredCodes = useMemo(() => {
    const q = (filter || "").trim().toLowerCase();
    let codes = sortedCodes;
    if (q) {
      codes = codes.filter((code) => {
        const label = (FLAG_LABELS[code] || code).toLowerCase();
        return code.toLowerCase().includes(q) || label.includes(q);
      });
      // Always sort filtered results by label
      codes = codes.slice().sort((a, b) => {
        const labelA = (FLAG_LABELS[a] || a).toLowerCase();
        const labelB = (FLAG_LABELS[b] || b).toLowerCase();
        return labelA.localeCompare(labelB);
      });
    }
    return codes;
  }, [filter, sortedCodes]);

  const listRef = useRef(null);

  // When modal opens, scroll the selected country into view if present
  useEffect(() => {
    if (!open || !selectedCode) return;
    // Wait for the list to render
    requestAnimationFrame(() => {
      const container = listRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-code="${selectedCode}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        try {
          el.scrollIntoView({ behavior: 'instant', block: 'center' });
        } catch (e) {
          // Fallback to instant scroll
          el.scrollIntoView();
        }
      }
    });
  }, [open, selectedCode, filteredCodes]);

  return (
    <Modal show={open} title="Pick a Country" onClose={onClose}>
      <div
        style={{
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          width: "min(80vw, 640px)",
          boxSizing: "border-box",
        }}
      >
        <div className="p-4 w-full">
          <div className="mb-3 flex gap-2 flex-wrap justify-center">
            <input
              type="text"
              placeholder="Filter by name or code (e.g. GB)"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border p-2 rounded w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            />
          </div>
          <div
            className="p-1 w-full"
            style={{ maxHeight: "calc(70vh - 6rem)", overflowY: "auto" }}
            ref={listRef}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filteredCodes.map((code) => (
                <button
                  key={code}
                  type="button"
                  data-code={code}
                  onClick={() => {
                    onSelect(code);
                    onClose();
                  }}
                  className={`flex items-center gap-2 p-2 rounded text-sm text-left ${
                    selectedCode === code
                      ? "ring-2 ring-blue-500"
                      : "border border-gray-200 dark:border-gray-700"
                  }`}
                >
                  <img
                    src={`/flags/${code}.png`}
                    alt={FLAG_LABELS[code] || code.toUpperCase()}
                    onError={(e) => {
                      try {
                        e.target.onerror = null;
                        e.target.src = `/flags/${code}.svg`;
                      } catch (e) {}
                    }}
                    className="w-6 h-4 object-contain"
                  />
                  <span className="truncate">
                    {FLAG_LABELS[code] || code.toUpperCase()}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default UserCountryPickerModal;
